import {
  Box,
  Container,
  Text,
  useColorMode,
  useColorModeValue,
  VStack,
  Heading,
  Grid,
  Button,
  HStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  IconButton,
  Tooltip,
  useToast,
  Tag,
  Select,
  Switch,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { Footer } from "components/layout/Footer";
import { PrivateNavbar } from "components/layout/PrivateNavbar";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { featureAPI } from "services/feature-api";
import { FeatureRequest, FeatureStatus, CreateFeatureRequest } from "types";
import {
  FiPlus,
  FiThumbsUp,
  FiX,
  FiEdit2,
  FiChevronRight,
} from "react-icons/fi";
import { useAuthStore } from "stores/authStore";

export const Features: React.FC = () => {
  const { colorMode } = useColorMode();
  const { t, i18n } = useTranslation();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { user } = useAuthStore();

  const [features, setFeatures] = useState<FeatureRequest[]>([]);
  const [userVotes, setUserVotes] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState<
    CreateFeatureRequest & { status?: FeatureStatus; isPublished?: boolean }
  >({
    title: "",
    description: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<FeatureStatus | null>(
    null
  );
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    loadFeatures();
    const userToken = localStorage.getItem("token");
    if (userToken) {
      loadUserVotes();
    }
  }, []);

  const loadFeatures = async () => {
    try {
      const userToken = localStorage.getItem("token");
      if (userToken) {
        const data = await featureAPI.getFeatures();
        setFeatures(data);
      } else {
        const data = await featureAPI.getPublicFeatures();
        setFeatures(data);
      }
    } catch (error) {
      console.error("Error loading features:", error);
      toast({
        title: t("features.error.loading"),
        status: "error",
        duration: 3000,
      });
    }
  };

  const loadUserVotes = async () => {
    try {
      const votes = await featureAPI.getUserVotes();
      setUserVotes(votes.map((v: any) => v.featureRequestId));
    } catch (error) {
      console.error("Error loading user votes:", error);
    }
  };

  const handleCreateFeature = async () => {
    if (!newFeature.title.trim() || !newFeature.description.trim()) return;

    setIsLoading(true);
    try {
      await featureAPI.createFeature(newFeature);
      onClose();
      loadFeatures();
      setNewFeature({ title: "", description: "" });
      toast({
        title: t("features.success.created"),
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error creating feature:", error);
      toast({
        title: t("features.error.creating"),
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditFeature = async () => {
    if (
      !newFeature.title.trim() ||
      !newFeature.description.trim() ||
      !editingFeatureId
    )
      return;

    setIsLoading(true);
    try {
      await featureAPI.updateFeature(editingFeatureId, newFeature);
      onClose();
      loadFeatures();
      setNewFeature({ title: "", description: "" });
      setIsEditMode(false);
      setEditingFeatureId(null);
      toast({
        title: t("features.success.updated"),
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error updating feature:", error);
      toast({
        title: t("features.error.updating"),
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEditModal = (feature: FeatureRequest) => {
    setNewFeature({
      title: feature.title,
      description: feature.description,
      status: feature.status,
      isPublished: feature.isPublished,
    });
    setIsEditMode(true);
    setEditingFeatureId(feature.id);
    onOpen();
  };

  const handleCloseModal = () => {
    setNewFeature({
      title: "",
      description: "",
      status: undefined,
      isPublished: undefined,
    });
    setIsEditMode(false);
    setEditingFeatureId(null);
    onClose();
  };

  const handleVote = async (id: string) => {
    try {
      if (userVotes.includes(id)) {
        await featureAPI.removeVote(id);
        setUserVotes(userVotes.filter((v) => v !== id));
      } else {
        await featureAPI.vote(id);
        setUserVotes([...userVotes, id]);
      }
      loadFeatures();
    } catch (error) {
      console.error("Error voting:", error);
      toast({
        title: t("features.error.voting"),
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await featureAPI.updateFeature(id, {
        status: "APPROVED" as FeatureStatus,
        isPublished: true,
      });
      loadFeatures();
    } catch (error) {
      console.error("Error approving feature:", error);
    }
  };

  const getStatusColor = (status: FeatureStatus) => {
    const colors = {
      PENDING: "yellow",
      APPROVED: "blue",
      REJECTED: "red",
      PLANNED: "purple",
      IN_PROGRESS: "orange",
      PAUSED: "gray",
      COMPLETED: "green",
      DECLINED: "gray",
    };
    return colors[status] || "gray";
  };

  const toggleDescription = (featureId: string) => {
    setExpandedDescriptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(featureId)) {
        newSet.delete(featureId);
      } else {
        newSet.add(featureId);
      }
      return newSet;
    });
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("features.title")}
              </Heading>
              <Text color="gray.500">
                {t("features.subtitle")} <br />
              </Text>
              <Tag>{t("features.subtitle2")}</Tag>
              {user && (
                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="brand"
                  onClick={onOpen}
                  alignSelf="center"
                >
                  {t("features.create")}
                </Button>
              )}
            </VStack>

            {/* Status Filter */}
            <Box
              overflowX="auto"
              py={4}
              sx={{
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: colorMode === "dark" ? "gray.600" : "gray.300",
                  borderRadius: "full",
                },
              }}
            >
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold" px={2}>
                  {t("features.filter")}:
                </Text>
                <Flex gap={2} minW="min-content" px={2}>
                  <Tag
                    size="md"
                    variant={selectedStatus === null ? "solid" : "outline"}
                    colorScheme="gray"
                    cursor="pointer"
                    onClick={() => setSelectedStatus(null)}
                    flexShrink={0}
                    minW="auto"
                  >
                    {t("features.status.ALL")}
                  </Tag>
                  {Object.values(FeatureStatus).map((status) => (
                    <Tag
                      key={status}
                      size="md"
                      variant={selectedStatus === status ? "solid" : "outline"}
                      colorScheme={getStatusColor(status)}
                      cursor="pointer"
                      onClick={() => setSelectedStatus(status)}
                      flexShrink={0}
                      minW="auto"
                    >
                      {t(`features.status.${status}`)}
                    </Tag>
                  ))}
                </Flex>
              </VStack>
            </Box>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={6}
            >
              {features
                .filter(
                  (feature) =>
                    selectedStatus === null || feature.status === selectedStatus
                )
                .map((feature) => (
                  <Box
                    key={feature.id}
                    p={6}
                    bg={cardBg}
                    borderRadius="xl"
                    boxShadow="sm"
                    _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
                    transition="all 0.2s"
                  >
                    <VStack align="stretch" spacing={4}>
                      <HStack justify="space-between">
                        <Heading size="md" noOfLines={2}>
                          {feature.title}
                        </Heading>
                        <Badge colorScheme={getStatusColor(feature.status)}>
                          {t(`features.status.${feature.status}`)}
                        </Badge>
                      </HStack>
                      <Text
                        noOfLines={
                          expandedDescriptions.has(feature.id) ? undefined : 3
                        }
                        color="gray.500"
                        cursor="pointer"
                        onClick={() => toggleDescription(feature.id)}
                        transition="all 0.2s"
                        _hover={{
                          color: colorMode === "dark" ? "gray.300" : "gray.700",
                        }}
                      >
                        {feature.description}
                      </Text>
                      {feature.description.length > 150 && (
                        <Text
                          fontSize="sm"
                          color="brand.primary"
                          cursor="pointer"
                          onClick={() => toggleDescription(feature.id)}
                          textAlign="right"
                        >
                          {expandedDescriptions.has(feature.id)
                            ? t("features.showLess")
                            : t("features.showMore")}
                        </Text>
                      )}
                      <HStack justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.500">
                          {new Date(feature.createdAt).toLocaleDateString(
                            i18n.language === "pt-BR" ? "pt-BR" : "en-US"
                          )}
                        </Text>
                        <HStack>
                          {user && user.isAdmin && (
                            <Badge colorScheme="purple">
                              {feature.voteCount} {t("features.votes")}
                            </Badge>
                          )}
                          {user &&
                            (user.isAdmin || feature.user.id === user?.id) && (
                              <Tooltip label={t("features.edit")}>
                                <IconButton
                                  aria-label="Edit"
                                  icon={<FiEdit2 />}
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleOpenEditModal(feature)}
                                />
                              </Tooltip>
                            )}
                          <Tooltip
                            label={
                              userVotes.includes(feature.id)
                                ? t("features.unvote")
                                : t("features.vote")
                            }
                          >
                            <IconButton
                              aria-label="Vote"
                              icon={
                                userVotes.includes(feature.id) ? (
                                  <FiX />
                                ) : (
                                  <FiThumbsUp />
                                )
                              }
                              size="sm"
                              colorScheme={
                                userVotes.includes(feature.id) ? "red" : "brand"
                              }
                              onClick={() => handleVote(feature.id)}
                            />
                          </Tooltip>
                        </HStack>
                      </HStack>
                      {user?.isAdmin && (
                        <Tag colorScheme="blue">By: {feature.user.email}</Tag>
                      )}
                      {user?.isAdmin && feature.status === "PENDING" && (
                        <Button
                          colorScheme="brand"
                          size="sm"
                          onClick={() => handleApprove(feature.id)}
                        >
                          {t("features.approve")}
                        </Button>
                      )}
                    </VStack>
                  </Box>
                ))}
            </Grid>

            {/* Status Flow Section */}
            <Box
              mt={8}
              p={4}
              borderRadius="md"
              shadow="sm"
              width="100%"
              overflowX="hidden"
            >
              <VStack align="stretch" spacing={4}>
                <Text fontSize="lg" fontWeight="bold">
                  {t("features.statusFlow")}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t("features.statusFlowDescription")}
                </Text>
                <Box overflowX="auto">
                  <HStack spacing={2} py={2} minWidth="min-content" px={2}>
                    <Tooltip label={t("features.status.PENDING_DESC")}>
                      <Tag colorScheme="gray" size="md" whiteSpace="nowrap">
                        {t("features.status.PENDING")}
                      </Tag>
                    </Tooltip>
                    <Icon as={FiChevronRight} flexShrink={0} />
                    <Tooltip label={t("features.status.APPROVED_DESC")}>
                      <Tag colorScheme="blue" size="md" whiteSpace="nowrap">
                        {t("features.status.APPROVED")}
                      </Tag>
                    </Tooltip>
                    <Icon as={FiChevronRight} flexShrink={0} />
                    <Tooltip label={t("features.status.PLANNED_DESC")}>
                      <Tag colorScheme="purple" size="md" whiteSpace="nowrap">
                        {t("features.status.PLANNED")}
                      </Tag>
                    </Tooltip>
                    <Icon as={FiChevronRight} flexShrink={0} />
                    <Tooltip label={t("features.status.IN_PROGRESS_DESC")}>
                      <Tag colorScheme="orange" size="md" whiteSpace="nowrap">
                        {t("features.status.IN_PROGRESS")}
                      </Tag>
                    </Tooltip>
                    <Icon as={FiChevronRight} flexShrink={0} />
                    <Tooltip label={t("features.status.COMPLETED_DESC")}>
                      <Tag colorScheme="green" size="md" whiteSpace="nowrap">
                        {t("features.status.COMPLETED")}
                      </Tag>
                    </Tooltip>
                  </HStack>
                </Box>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Create/Edit Feature Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditMode
              ? t("features.modal.titleEdit")
              : t("features.modal.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t("features.modal.titleLabel")}</FormLabel>
                <Input
                  value={newFeature.title}
                  onChange={(e) =>
                    setNewFeature({ ...newFeature, title: e.target.value })
                  }
                  placeholder={t("features.modal.titlePlaceholder")}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>{t("features.modal.descriptionLabel")}</FormLabel>
                <Textarea
                  value={newFeature.description}
                  onChange={(e) =>
                    setNewFeature({
                      ...newFeature,
                      description: e.target.value,
                    })
                  }
                  placeholder={t("features.modal.descriptionPlaceholder")}
                  rows={4}
                />
              </FormControl>

              {user?.isAdmin && isEditMode && (
                <>
                  <FormControl>
                    <FormLabel>{t("features.modal.statusLabel")}</FormLabel>
                    <Select
                      value={newFeature.status}
                      onChange={(e) =>
                        setNewFeature({
                          ...newFeature,
                          status: e.target.value as FeatureStatus,
                        })
                      }
                    >
                      {Object.values(FeatureStatus).map((status) => (
                        <option key={status} value={status}>
                          {t(`features.status.${status}`)}
                        </option>
                      ))}
                    </Select>
                    <FormControl display="flex" alignItems="center" mt={2}>
                      <FormLabel mb="0">
                        {t("features.modal.isPublished")}
                      </FormLabel>
                      <Switch
                        isChecked={newFeature.isPublished}
                        onChange={(e) =>
                          setNewFeature({
                            ...newFeature,
                            isPublished: e.target.checked,
                          })
                        }
                      />
                    </FormControl>
                  </FormControl>
                </>
              )}

              <Text fontSize="sm" color="gray.500">
                {t("features.modal.note")}
              </Text>

              <Button
                colorScheme="brand"
                mr={3}
                onClick={isEditMode ? handleEditFeature : handleCreateFeature}
                width="full"
                isLoading={isLoading}
              >
                {isEditMode
                  ? t("features.modal.update")
                  : t("features.modal.submit")}
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Footer />
    </Box>
  );
};
